"use client";
import { buttonVariants } from "@/components/ui/button";
import {
  Action,
  ControlType,
} from "@/context/AnalyticsProvider/AnalyticsProvider";
import { GamePageSection } from "@/context/AnalyticsProvider/gamePage";
import { useGameDetailsContext } from "@/context/GameDetailsContext";
import { useGameOnboardingContext } from "@/context/GameOnboardingContext";
import { cn } from "@/lib/utils";
import { trackPixelEvent } from "@/utils/pixel";
import { useCallback, useEffect, useMemo } from "react";
import { SteamWishlistButton } from "./SteamWishlistButton";
import type { GPOnboardingStepProps } from "./utils";

export const WishlistSteam = ({
  onNext,
  isVisible,
  track,
}: GPOnboardingStepProps) => {
  const { startSocialQuest, validateSocialQuest, questState } =
    useGameOnboardingContext();
  const { allQuests, gameData } = useGameDetailsContext();
  const steamWishlistQuest = useMemo(
    () => allQuests?.find((quest) => quest.type === "steam_wishlist"),
    [allQuests],
  );
  // TODO: return metadata from quests API and get this dynamically
  const steamAppId = 3760100;

  const handleClick = useCallback(async () => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "steam-wishlist",
        controlType: ControlType.BUTTON,
        controlLabel: "Wishlist on Steam",
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });
    // Pixel event for tracking intent
    trackPixelEvent("SteamWishlistCTA", {});

    // Always proceed to next step after a short delay to allow the popup to open
    setTimeout(() => {
      onNext();
    }, 1000);
  }, [track, onNext]);

  useEffect(() => {
    if (!questState?.error) return;
    track({
      action: Action.FAILED,
      extras: {
        control: "steam-wishlist",
        controlLabel: questState.error,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });
  }, [questState, track]);

  return (
    <div className="flex-grow flex flex-col items-center justify-between gap-y-10">
      {/* Title */}
      <div>
        <h2 className="text-[clamp(1.25rem,6.25vw,1.875rem)] font-bold leading-[clamp(1.5rem,6.25vw,2.25rem)] text-center">
          Wishlist the game on Steam
        </h2>
      </div>

      {/* Buttons */}
      <div className="flex flex-col items-center gap-y-6">
        {questState?.error ? (
          <p className="text-sm text-red-500">
            Make sure you have wishlisted. If you have wishlisted make sure your
            Steam profile is set to public.
          </p>
        ) : null}

        {/* Steam Widget Button */}
        {/* Wrapper to capture click for analytics since iframe swallows it */}
        <div
          className="relative z-10 flex justify-center group"
          style={{ width: 209, height: 48 }}
        >
          <SteamWishlistButton
            appId={steamAppId.toString()}
            gameStatus="unreleased"
            scale={1.5}
            utmSource="immutable-play"
            utmCampaign="immutable-onboarding"
            label="Wishlist on Steam"
            onClick={handleClick}
            buttonClassName={cn(
              buttonVariants({ variant: "default", size: "lg" }),
              "w-full h-full rounded-[26px] bg-text-body-primary text-primary-foreground hover:shadow-sm group-hover:shadow-sm group-active:opacity-60 shadow-sm font-roboto font-medium",
            )}
          />
        </div>
      </div>
    </div>
  );
};
